/**
 * client/src/components/VoicePicker.tsx — who reads this book, and what changing that costs.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * THE PRICE IS ON THE BUTTON, NOT IN A REGRET.
 *
 * Changing a book's narrator does not invalidate a cache — it asks for a **different recording of the
 * whole book**. `clipKey` names the voice (`shared/narration/keys.ts`), so every sentence of the book
 * becomes a clip that does not exist yet. On the live shelf that is ~43 GPU-minutes for *Alice*, ~2.8
 * hours for *Sherlock*, and about **nineteen hours** for the Bible, against a daily unattended ceiling
 * of ninety minutes.
 *
 * So this component quotes before it spends, from `verifyCoverage()` — the planner's own answer, not
 * an estimate — and the quote names pages and minutes rather than a percentage. The rule it is built
 * on is AGENTS.md's: *"no code path may raise a book above one-page granularity on its own
 * initiative"*. **Changing the narrator enqueues nothing.** It re-plans, and the existing priced
 * buttons (a chapter, a room's preparation, pressing play) are how the pages actually get made.
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ **Switching back is free, and the copy says so**, because it is the thing that makes trying a
 * voice safe: `sentence_clips` is keyed per voice, so the old narrator's clips are never deleted and
 * a book that has been read in two voices can be flipped between them with no GPU at all. The quote
 * shows `0 pages need the voice` in that case, which is the same number arriving from the same walk.
 *
 * ⚠️ **A voice that cannot be used is shown WITH ITS REASON rather than omitted.** An option missing
 * from a list is a question nobody can answer; *"spark-1 serves `hindi` and AB_TTS_HOSTS maps nothing
 * to it"* is one somebody can act on in a minute. Same rule as every refusal in this product.
 *
 * ⚠️ **No new CSS.** Every class here already exists in `client/src/ui/app.css`.
 */

import { useCallback, useEffect, useState, type ReactElement } from "react";

import { ApiError, api } from "../lib/api.ts";

/* ── the wire — `routes/voices.ts`, exactly ───────────────────────────────── */

export interface PickerVoice {
  id: string;
  label: string;
  kind: string;
  isDefault: boolean;
  /** Null when nobody has auditioned it, or when this actor may not fetch the sample. */
  sampleUrl: string | null;
}

export interface NarratorQuote {
  voiceId: string;
  label: string;
  editionId: string;
  pagesAudible: number;
  pagesReady: number;
  pagesNeedingGpu: number;
  pagesNeedingStitch: number;
  estGpuSec: number;
  estGpuMin: number;
  free: boolean;
}

export interface BookVoiceResponse {
  bookId: string;
  /** NULL means "follow the server default" — a different statement from naming today's default. */
  narratorVoiceId: string | null;
  effective: PickerVoice;
  serverDefault: PickerVoice;
  voices: PickerVoice[];
  unavailable: { id: string; label: string; reason: string | null }[];
  quote: NarratorQuote | null;
}

/* ── the component ────────────────────────────────────────────────────────── */

export function VoicePicker({ bookId }: { bookId: string }): ReactElement | null {
  const [data, setData] = useState<BookVoiceResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const [quote, setQuote] = useState<NarratorQuote | null>(null);
  const [pricing, setPricing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [said, setSaid] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const got = await api.get<BookVoiceResponse>(`/books/${encodeURIComponent(bookId)}/voice`);
      setData(got);
      setChosen(got.narratorVoiceId);
      setError(null);
    } catch (err) {
      // A 404 here is LAW 17's refusal: not your book. The component renders nothing rather than an
      // empty panel, because a panel that says "you cannot change this" on somebody else's book is a
      // 403 in HTML.
      if (err instanceof ApiError && err.notFound) setError(null);
      else setError(err instanceof ApiError ? err.message : "Couldn't ask who reads this book.");
      setData(null);
    }
  }, [bookId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Price on selection, before anything is written.
   *
   * One request per choice and none on mount — `verifyCoverage()` walks the whole edition through the
   * planner, which is affordable on a button and would not be on a render.
   */
  const price = useCallback(
    async (voiceId: string | null) => {
      setChosen(voiceId);
      setSaid(null);
      setQuote(null);
      if (data === null) return;
      const target = voiceId ?? data.serverDefault.id;
      if (target === data.effective.id) return;
      setPricing(true);
      try {
        const got = await api.get<BookVoiceResponse>(
          `/books/${encodeURIComponent(bookId)}/voice?voiceId=${encodeURIComponent(target)}`,
        );
        setQuote(got.quote);
      } catch (err) {
        setSaid(err instanceof ApiError ? err.message : "Couldn't work out what that would cost.");
      } finally {
        setPricing(false);
      }
    },
    [bookId, data],
  );

  const apply = useCallback(async () => {
    setSaving(true);
    setSaid(null);
    try {
      const got = await api.patch<{ applied: boolean; quote: NarratorQuote | null; effective: PickerVoice }>(
        `/books/${encodeURIComponent(bookId)}/voice`,
        { voiceId: chosen, apply: true },
      );
      setQuote(got.quote);
      setSaid(
        got.quote === null || got.quote.pagesNeedingGpu === 0
          ? `${got.effective.label} reads this book now. Nothing needs to be made — it has been read in this voice before.`
          : `${got.effective.label} reads this book now. ${got.quote.pagesNeedingGpu} pages need the voice; nothing has been queued, so press play or narrate a chapter when you want them.`,
      );
      await load();
    } catch (err) {
      setSaid(err instanceof ApiError ? err.message : "Couldn't change the voice.");
    } finally {
      setSaving(false);
    }
  }, [bookId, chosen, load]);

  if (error !== null) return <section className="ab-panel ab-panel-bad">{error}</section>;
  if (data === null) return null;

  const target = chosen ?? data.serverDefault.id;
  const changed = target !== data.effective.id;
  const sample = data.voices.find((v) => v.id === target)?.sampleUrl ?? null;

  return (
    <section className="ab-panel">
      <h2 className="ab-h2">Who reads it</h2>
      <p className="ab-lede">
        {data.effective.label} reads this book
        {data.narratorVoiceId === null ? (
          <> — because that is this server&rsquo;s default voice, and this book has never been given one of its own.</>
        ) : (
          <>.</>
        )}
      </p>

      <div className="ab-field ab-select">
        <label htmlFor="ab-narrator">Narrator</label>
        <select
          id="ab-narrator"
          value={chosen ?? ""}
          disabled={saving}
          onChange={(e) => void price(e.target.value === "" ? null : e.target.value)}
        >
          {/* The empty option is NOT "no voice" — it is "follow the server default", which keeps
              tracking `AB_TTS_VOICE` if that ever moves. Pinning today's default explicitly is the
              other option in the list, and the two are different promises. */}
          <option value="">Follow the server default ({data.serverDefault.label})</option>
          {data.voices.map((v) => (
            <option key={v.id} value={v.id}>
              {v.label}
              {v.kind === "base" ? " — the stock voice, not a person" : ""}
            </option>
          ))}
        </select>
      </div>

      {sample !== null && (
        <p className="ab-hint">
          {/* The stored audition, served off disk. No GPU is spent by pressing play here — the
              generating button lives on /admin, behind the hourly meter. */}
          <audio preload="none" controls src={sample} aria-label={`${data.effective.label} reading a line`} />
        </p>
      )}

      {pricing && <p className="ab-hint">Working out what that would cost…</p>}

      {changed && quote !== null && (
        <div className="ab-note ab-note-warn">
          <span>
            {quote.free ? (
              <>
                <strong>{quote.label} has read this book before.</strong> Nothing would have to be made — the clips are
                still on disk, so switching is free and instant.
              </>
            ) : (
              <>
                <strong>
                  {quote.pagesNeedingGpu} of {quote.pagesAudible} pages would have to be read again
                </strong>{" "}
                in {quote.label} — about {quote.estGpuMin < 1 ? "under a minute" : `${quote.estGpuMin} minutes`} of the
                voice machine
                {quote.pagesNeedingStitch > 0 ? `, plus ${quote.pagesNeedingStitch} pages that only need re-joining` : ""}.
                {quote.pagesReady > 0 ? ` ${quote.pagesReady} pages are already read in this voice.` : ""} Nothing is
                queued by changing this: the pages are made when you press play, narrate a chapter, or prepare a room.
              </>
            )}
          </span>
        </div>
      )}

      <div className="ab-actions">
        <button type="button" className="ab-btn ab-btn-primary" disabled={!changed || saving || pricing} onClick={() => void apply()}>
          {saving ? "Changing…" : changed ? "Use this voice" : "This is the voice"}
        </button>
        {changed && (
          <button type="button" className="ab-btn ab-btn-ghost" disabled={saving} onClick={() => void price(data.narratorVoiceId)}>
            Cancel
          </button>
        )}
      </div>

      {said !== null && <p className="ab-hint">{said}</p>}

      {data.unavailable.length > 0 && (
        <>
          <h3 className="ab-h3">Not offered</h3>
          {/* Shown rather than hidden. A voice missing from the list above with no explanation is how
              somebody concludes the clone did not work and re-records it. */}
          <ul className="ab-warnings">
            {data.unavailable.map((v) => (
              <li key={v.id}>
                <strong>{v.label}</strong> — {v.reason ?? "unavailable"}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
